import { useCallback, useMemo, useState } from 'react';

import {
  type ModelMessageTemplate,
  useDeleteMutation,
  useLazyGetSearchResultQuery,
  useSearchMutation,
} from '@app/api/MessageTemplates';
import { SUCCESS } from '@app/constants';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

export const useMessageTemplatesRegistry = () => {
  const { showNotification } = useNotification();

  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const { isMobile } = useWindowSize();

  const [templates, setTemplates] = useState<ModelMessageTemplate[]>([]);
  const [searchId, setSearchId] = useState(0);
  const [activeTemplateId, setActiveTemplateId] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowsTotal, setRowsTotal] = useState(10);

  const [isDeletionWindowOpen, setIsDeletionWindowOpen] = useState(false);

  const [deleteMessageTemplate] = useDeleteMutation();
  const [getMessageTemplates] = useSearchMutation();
  const [getSearchResultFilteredByPage] = useLazyGetSearchResultQuery();

  const {
    userAccessRights: { MessageTemplates },
  } = useNewUserAccessRights();

  const hasUserRightToMessageTemplatesModule = useMemo(
    () => MessageTemplates?.IsOn,
    [MessageTemplates]
  );

  const deleteTemplate = useCallback(
    async (templateId: number) => {
      try {
        await deleteMessageTemplate({
          ActiveBranchId,
          data: [templateId],
        });

        const { MessageTemplates: updatedTemplates, CountMessageTemplates } =
          await getMessageTemplates({
            ActiveBranchId,
            data: {
              SearchTerm: null,
              MessageTemplatesPerPage: pageSize,
            },
          }).unwrap();

        showNotification('Шаблон сообщения удален успешно', SUCCESS);

        setRowsTotal(CountMessageTemplates);
        setTemplates(updatedTemplates || []);
      } catch (error) {
        showNotification(error);
      } finally {
        setIsDeletionWindowOpen(false);
      }
    },
    [
      ActiveBranchId,
      deleteMessageTemplate,
      getMessageTemplates,
      showNotification,
      pageSize,
    ]
  );

  const changePageNumber = useCallback(
    async (page: number, pageSize: number) => {
      try {
        const { MessageTemplates: pageTemplates } =
          await getSearchResultFilteredByPage({
            ActiveBranchId,
            searchId,
            page,
            rowsPerPage: pageSize,
          }).unwrap();

        setTemplates(pageTemplates || []);
      } catch (error) {
        showNotification(error);
      }
    },
    [ActiveBranchId, getSearchResultFilteredByPage, showNotification, searchId]
  );

  const handleChange = useCallback(
    (page: number, pageSize: number) => {
      setPage(page);
      setPageSize(pageSize);
      changePageNumber(page, pageSize);
    },
    [changePageNumber]
  );

  return {
    page,
    pageSize,
    templates,
    setTemplates,
    isDeletionWindowOpen,
    setIsDeletionWindowOpen,
    deleteTemplate,
    hasUserRightToMessageTemplatesModule,
    handleChange,
    setSearchId,
    rowsTotal,
    setRowsTotal,
    activeTemplateId,
    setActiveTemplateId,
    isMobile,
  };
};
